import { ACMEResponse } from '@b2b/types/core';
import angular, { IHttpService } from 'angular';
import { BehaviorSubject, map } from 'rxjs';
import { ThemesHelperService } from '../themesHelper/types';
import { ActiveApp, DEFAULT_STORED_VALUE_APP_ID } from './types';

export interface AppsState {
  status: 'idle' | 'loading' | 'error' | 'loaded';
  data: ActiveApp[] | null;
}

/**
 * Loads the tenant's active apps (`GET /v2/b2b/apps/activeapps`) and exposes
 * helpers to check which are active. Used to gate features such as gift card
 * redemption behind the stored value app being active for the tenant.
 */
export class AppsService {
  static $inject = ['$http', 'ThemesHelperService'];

  private stateSubject = new BehaviorSubject<AppsState>({ status: 'idle', data: null });
  state$ = this.stateSubject.asObservable();
  activeApps$ = this.stateSubject.pipe(map((model) => (model.status === 'loaded' ? model.data ?? [] : [])));

  constructor(
    private $http: IHttpService,
    private themesHelperService: ThemesHelperService,
  ) {}

  /**
   * The configured stored value app id. Overridable via
   * `window.CONFIG.STORED_VALUE_APP_ID`, defaulting to `stored-value`.
   */
  getStoredValueAppId(): string {
    return (typeof window !== 'undefined' && window.CONFIG?.STORED_VALUE_APP_ID) || DEFAULT_STORED_VALUE_APP_ID;
  }

  /** True when an app with the given ref id is active for the tenant. */
  hasActiveApp(appRefId: string): boolean {
    return (this.stateSubject.value.data ?? []).some((app) => app.appRefid === appRefId);
  }

  /** True when the stored value (gift card) app is active for the tenant. */
  hasStoredValueApp(): boolean {
    return this.hasActiveApp(this.getStoredValueAppId());
  }

  /** Loads active apps once; subsequent calls while loading/loaded are no-ops. */
  ensureLoaded(): void {
    const { status } = this.stateSubject.value;
    if (status === 'loading' || status === 'loaded') {
      return;
    }
    this.loadActiveApps();
  }

  loadActiveApps(): void {
    const tenantId = this.themesHelperService.getCompany('tenantId');

    if (!tenantId) {
      this.stateSubject.next({ status: 'error', data: null });
      return;
    }

    this.stateSubject.next({ status: 'loading', data: this.stateSubject.value.data });

    this.$http
      .get<ACMEResponse<ActiveApp[]>>('/v2/api/b2b/apps/activeapps', {
        headers: {
          'x-tenant-id': String(tenantId),
        },
      })
      .then((response) => {
        this.stateSubject.next({ status: 'loaded', data: response.data.payload ?? [] });
      })
      .catch(() => {
        this.stateSubject.next({ status: 'error', data: null });
      });
  }
}

export const AppsServiceModule = angular
  .module('acme.common.service.apps', [])
  .service('AppsService', AppsService).name;
